import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Flex, Grid, Heading, Text, Button, Spinner, Alert, AlertIcon, AlertDescription, Link, Textarea,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton, useToast,
  Table, Thead, Tbody, Tr, Th, Td, Divider,
} from '@chakra-ui/react';
import { FiExternalLink, FiRefreshCw, FiCheckCircle, FiFileText } from 'react-icons/fi';
import { StatusPill } from 'components/monitoring/ai/common';
import {
  VERSION_STATUS, DRIVE_STATE, listDocuments, checkDocument, markReady, getVersion, discardVersion, apiError,
} from 'api/administrador/ventanillaDocuments';

/**
 * Admin › Ventanilla › Configuración y documentos (etapa 1). Muestra qué versión del manual y de las
 * preguntas frecuentes usa el agente, si Andrea cambió algo en Drive y permite congelar una versión
 * para revisión. Nada de lo que se hace aquí cambia al agente: eso solo pasa con un despliegue.
 */

const fmt = (iso) => (iso
  ? new Date(iso).toLocaleString('es-CO', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' })
  : '—');

/** Acción con aspecto de enlace (botón, para que funcione con teclado). */
function LinkButton({ color = 'purple', ...props }) {
  return <Button variant="link" size="sm" fontSize="13px" fontWeight="500" colorScheme={color} {...props} />;
}

function Field({ label, children }) {
  return (
    <Flex justify="space-between" gap={4} py={1.5} fontSize="13px" borderBottom="1px solid" borderColor="gray.100">
      <Text color="gray.600" flexShrink={0}>{label}</Text>
      <Box textAlign="right" minW={0}>{children}</Box>
    </Flex>
  );
}

function VersionModal({ id, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!id) return;
    setData(null);
    setError(null);
    getVersion(id).then(setData).catch((e) => setError(apiError(e)));
  }, [id]);
  return (
    <Modal isOpen={Boolean(id)} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="16px">
          {data ? `${data.document?.title} · versión ${data.number}` : 'Copia guardada'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {error && <Alert status="error" borderRadius="md"><AlertIcon />{error}</Alert>}
          {!data && !error && <Flex justify="center" py={10}><Spinner /></Flex>}
          {data && (
            <>
              <Text fontSize="12px" color="gray.600" mb={3}>
                Copia exacta guardada el {fmt(data.markedAt)} por {data.markedBy || '—'}. No cambia aunque se edite el documento en Drive.
              </Text>
              <Box as="pre" whiteSpace="pre-wrap" fontFamily="inherit" fontSize="13px" bg="gray.50" p={4} borderRadius="md" border="1px solid" borderColor="gray.200">
                {data.content}
              </Box>
            </>
          )}
        </ModalBody>
        <ModalFooter><Button size="sm" onClick={onClose}>Cerrar</Button></ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function MarkModal({ doc, onClose, onDone }) {
  const toast = useToast();
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (doc) setNote(''); }, [doc]);
  const replaces = doc?.inReview;

  const submit = async () => {
    setSaving(true);
    try {
      const updated = await markReady(doc.key, note);
      toast({ status: 'success', title: `Versión ${updated.inReview?.number} lista para revisión`, description: 'El agente sigue usando la versión activa.', duration: 6000 });
      onDone(updated);
    } catch (e) {
      toast({ status: 'error', title: 'No se pudo marcar', description: apiError(e), duration: 8000 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={Boolean(doc)} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="16px">Marcar lista para revisión</ModalHeader>
        <ModalCloseButton />
        <ModalBody fontSize="14px">
          <Text mb={3}>
            Se guarda una copia exacta de «{doc?.title}» tal como está ahora en Drive. Úsalo cuando termines de editar.
          </Text>
          <Alert status="info" borderRadius="md" mb={3} fontSize="13px">
            <AlertIcon />
            <AlertDescription>
              Marcarla <b>no cambia nada en el agente</b>. Los cambios se revisan, se prueban en el simulador y solo llegan al agente con un despliegue.
            </AlertDescription>
          </Alert>
          {replaces && (
            <Alert status="warning" borderRadius="md" mb={3} fontSize="13px">
              <AlertIcon />
              <AlertDescription>
                La versión {replaces.number}, que está en revisión, quedará como «Reemplazada».
              </AlertDescription>
            </Alert>
          )}
          <Text as="label" htmlFor="mark-note" fontSize="13px" color="gray.700">Qué cambió (opcional)</Text>
          <Textarea id="mark-note" mt={1} size="sm" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Por ejemplo: cambié «certificado de estudio» por «certificado de notas»." />
        </ModalBody>
        <ModalFooter gap={2}>
          <Button size="sm" variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button size="sm" colorScheme="purple" onClick={submit} isLoading={saving}>Marcar lista para revisión</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function DocumentCard({ doc, connection, onChange, onMark, onView }) {
  const toast = useToast();
  const [checking, setChecking] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const state = DRIVE_STATE[doc.drive.state] || DRIVE_STATE.sin_revisar;
  const canMark = connection.configured && !['sin_cambios', 'igual_a_revision'].includes(doc.drive.state);

  const check = async () => {
    setChecking(true);
    try {
      onChange(await checkDocument(doc.key));
    } catch (e) {
      toast({ status: 'error', title: 'No se pudo revisar Drive', description: apiError(e), duration: 8000 });
    } finally {
      setChecking(false);
    }
  };

  const discard = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`¿Descartar la versión ${doc.inReview.number}? Queda en el historial como «Descartada».`)) return;
    setDiscarding(true);
    try {
      onChange(await discardVersion(doc.inReview.id));
    } catch (e) {
      toast({ status: 'error', title: 'No se pudo descartar', description: apiError(e), duration: 8000 });
    } finally {
      setDiscarding(false);
    }
  };

  return (
    <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="12px" p={5}>
      <Flex justify="space-between" align="flex-start" gap={3} mb={1}>
        <Box minW={0}>
          <Heading as="h2" fontSize="17px" fontWeight="700">{doc.title}</Heading>
          <Text fontSize="12px" color="gray.500" mt={0.5}>Llega al agente por: {doc.usedIn}</Text>
        </Box>
        <StatusPill label={state.label} tone={state.tone} />
      </Flex>

      {(state.text || doc.drive.error) && (
        <Alert status={state.tone === 'red' ? 'error' : state.tone === 'orange' ? 'warning' : 'info'} borderRadius="md" fontSize="13px" mt={3} py={2}>
          <AlertIcon />
          <AlertDescription>{doc.drive.error || state.text}</AlertDescription>
        </Alert>
      )}
      {doc.warnings.map((w) => (
        <Alert key={w} status="error" borderRadius="md" fontSize="13px" mt={2} py={2}><AlertIcon /><AlertDescription>{w}</AlertDescription></Alert>
      ))}

      <Text fontSize="12px" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing=".04em" mt={4} mb={1}>
        Versión activa (la usa el agente)
      </Text>
      {doc.active ? (
        <>
          <Field label="Versión">
            <Flex gap={2} align="center" justify="flex-end">
              <Text fontWeight="600">{doc.active.number}</Text>
              {doc.active.applied && <StatusPill label="Aplicada" tone="green" />}
            </Flex>
          </Field>
          <Field label="Origen">{doc.active.sourceLabel || doc.active.source || '—'}</Field>
          <Field label="Texto"><LinkButton onClick={() => onView(doc.active.id)}>Ver copia</LinkButton></Field>
        </>
      ) : <Text fontSize="13px" color="gray.600">Sin versión activa.</Text>}

      <Text fontSize="12px" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing=".04em" mt={4} mb={1}>
        En revisión
      </Text>
      {doc.inReview ? (
        <>
          <Field label="Versión">
            <Flex gap={2} align="center" justify="flex-end">
              <Text fontWeight="600">{doc.inReview.number}</Text>
              <StatusPill label={VERSION_STATUS[doc.inReview.status]?.label || doc.inReview.status} tone={VERSION_STATUS[doc.inReview.status]?.tone} />
            </Flex>
          </Field>
          <Field label="Marcada">{fmt(doc.inReview.markedAt)} · {doc.inReview.markedBy || '—'}</Field>
          {doc.inReview.note && <Field label="Nota"><Text whiteSpace="pre-wrap">{doc.inReview.note}</Text></Field>}
          <Text fontSize="12px" color="gray.600" mt={2}>El agente todavía no usa esta versión.</Text>
          <Flex gap={3} mt={1} fontSize="13px">
            <LinkButton onClick={() => onView(doc.inReview.id)}>Ver copia</LinkButton>
            <LinkButton color="red" onClick={discard} isLoading={discarding}>Descartar</LinkButton>
          </Flex>
        </>
      ) : <Text fontSize="13px" color="gray.600">Ninguna.</Text>}

      <Text fontSize="12px" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing=".04em" mt={4} mb={1}>
        Google Docs
      </Text>
      <Field label="Documento">
        <Link href={doc.drive.url} isExternal color="purple.600" display="inline-flex" alignItems="center" gap={1}>
          Abrir en Google Docs <FiExternalLink />
        </Link>
      </Field>
      <Field label="Última edición">{fmt(doc.drive.modifiedAt)}{doc.drive.modifiedBy ? ` · ${doc.drive.modifiedBy}` : ''}</Field>
      <Field label="Revisado">{fmt(doc.drive.checkedAt)}</Field>

      <Flex gap={2} mt={4} wrap="wrap">
        <Button size="sm" leftIcon={<FiRefreshCw />} variant="outline" onClick={check} isLoading={checking} isDisabled={!connection.configured}>
          Revisar Drive ahora
        </Button>
        <Button size="sm" leftIcon={<FiCheckCircle />} colorScheme="purple" onClick={() => onMark(doc)} isDisabled={!canMark}>
          Marcar lista para revisión
        </Button>
      </Flex>

      {doc.versions.length > 1 && (
        <>
          <Divider my={4} />
          <Text fontSize="12px" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing=".04em" mb={1}>Historial</Text>
          <Box overflowX="auto">
            <Table size="sm">
              <Thead><Tr><Th px={2}>Versión</Th><Th px={2}>Estado</Th><Th px={2}>Marcada</Th><Th px={2} /></Tr></Thead>
              <Tbody>
                {doc.versions.map((v) => (
                  <Tr key={v.id} fontSize="13px">
                    <Td px={2}>{v.number}</Td>
                    <Td px={2}>
                      <StatusPill label={v.applied ? 'Aplicada' : VERSION_STATUS[v.status]?.label || v.status} tone={v.applied ? 'green' : VERSION_STATUS[v.status]?.tone} />
                    </Td>
                    <Td px={2}>{fmt(v.markedAt)}<Text color="gray.500" fontSize="12px">{v.markedBy}</Text></Td>
                    <Td px={2}><LinkButton onClick={() => onView(v.id)} aria-label={`Ver copia de la versión ${v.number}`}><FiFileText /></LinkButton></Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </>
      )}
    </Box>
  );
}

export default function VentanillaDocumentos() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [marking, setMarking] = useState(null);
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    try {
      setData(await listDocuments());
      setError(null);
    } catch (e) {
      setError(apiError(e, 'No se pudieron cargar los documentos.'));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const replaceDoc = (doc) => setData((d) => ({ ...d, data: d.data.map((x) => (x.key === doc.key ? doc : x)) }));

  return (
    <Box p={{ base: 3, md: 6 }} maxW="1200px">
      <Heading fontSize="22px" fontWeight="700" letterSpacing="-.01em">Configuración y documentos</Heading>
      <Text color="gray.600" fontSize="14px" mt={1} maxW="760px">
        Los documentos del área se editan en Google Docs. Aquí se ve qué versión usa el agente y si hay cambios.
        Cuando termines de editar, marca el documento listo para revisión: se guarda una copia, se revisa y se prueba, y
        solo llega al agente con un despliegue.
      </Text>

      {error && <Alert status="error" borderRadius="md" mt={4}><AlertIcon />{error}</Alert>}
      {!data && !error && <Flex justify="center" py={16}><Spinner /></Flex>}

      {data && (
        <>
          {data.connection.message && (
            <Alert status={data.connection.configured ? 'info' : 'warning'} borderRadius="md" mt={4} fontSize="13px">
              <AlertIcon />
              <AlertDescription>{data.connection.message}</AlertDescription>
            </Alert>
          )}
          <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={4} mt={4}>
            {data.data.map((doc) => (
              <DocumentCard key={doc.key} doc={doc} connection={data.connection} onChange={replaceDoc} onMark={setMarking} onView={setViewing} />
            ))}
          </Grid>
          <Text fontSize="12px" color="gray.500" mt={4}>
            En este servidor corre el guion {data.running.guion}
            {Object.entries(data.running.documents).map(([key, v]) => ` · ${key === 'faq' ? 'preguntas frecuentes' : key}: versión ${v.version}`).join('')}.
            {data.connection.serviceAccount && ` Drive se lee con ${data.connection.serviceAccount}.`}
          </Text>
        </>
      )}

      <MarkModal doc={marking} onClose={() => setMarking(null)} onDone={(doc) => { replaceDoc(doc); setMarking(null); }} />
      <VersionModal id={viewing} onClose={() => setViewing(null)} />
    </Box>
  );
}
